/**
 * Indigo Switch for the design system.
 * A customizable switch component built on top of Ant Design Switch.
 *
 * Features:
 * - Compact size with design system styling
 * - Black color scheme using design system variables
 * - Disabled states
 * - Loading states
 * - TypeScript support
 *
 * Usage:
 *   <Switch
 *     checked={isEnabled}
 *     onChange={handleChange}
 *     disabled={false}
 *   />
 */
import { Switch as AntSwitch } from "antd";
import { cva, type VariantProps } from "class-variance-authority";
import {
  ComponentRef,
  ForwardedRef,
  forwardRef,
  ReactElement,
  Ref,
} from "react";
import "./Switch.css";

export interface SwitchProps extends VariantProps<typeof switchVariants> {
  /**
   * Whether the switch is checked
   */
  checked?: boolean;

  /**
   * Initial checked state for uncontrolled usage
   */
  defaultChecked?: boolean;

  /**
   * Callback fired when the switch state changes
   */
  onChange?: (checked: boolean) => void;

  /**
   * Whether the switch is disabled
   */
  disabled?: boolean;

  /**
   * Whether the switch is in loading state
   */
  loading?: boolean;

  /**
   * Size of the switch
   */
  size?: "small" | "default";

  /**
   * Additional CSS class
   */
  className?: string;

  /**
   * Test ID for the component
   */
  "data-testid"?: string;

  /**
   * Content to be shown when the switch is checked
   */
  checkedChildren?: React.ReactNode;

  /**
   * Content to be shown when the switch is unchecked
   */
  unCheckedChildren?: React.ReactNode;

  /**
   * Label to show on the left side (unchecked state)
   */
  leftLabel?: string;

  /**
   * Label to show on the right side (checked state)
   */
  rightLabel?: string;
}

/**
 * switchVariants - CVA configuration for Switch component variants
 * Uses CSS classes that map to our design tokens
 */
const switchVariants = cva("indigo-switch-base", {
  variants: {
    size: {
      small: "indigo-switch-small",
      default: "indigo-switch-default",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

// Create a functional component for our switch
function InternalSwitch(
  {
    checked,
    defaultChecked,
    onChange,
    disabled = false,
    loading = false,
    size = "default",
    className = "",
    "data-testid": dataTestId,
    checkedChildren,
    unCheckedChildren,
    leftLabel,
    rightLabel,
  }: SwitchProps,
  ref: ForwardedRef<ComponentRef<typeof AntSwitch>>,
) {
  // Apply variant styles
  const variantClass = switchVariants({ size });

  // If labels are provided, render the switch with labels
  if (leftLabel || rightLabel) {
    return (
      <div className={`indigo-switch-with-labels ${variantClass} ${className}`}>
        {leftLabel && (
          <span
            className={`indigo-switch-label indigo-switch-label-left ${!checked ? "indigo-switch-label-active" : ""}`}
          >
            {leftLabel}
          </span>
        )}
        <AntSwitch
          checked={checked}
          defaultChecked={defaultChecked}
          onChange={onChange}
          disabled={disabled}
          loading={loading}
          size={size}
          checkedChildren={checkedChildren}
          unCheckedChildren={unCheckedChildren}
          ref={ref}
          data-testid={dataTestId}
        />
        {rightLabel && (
          <span
            className={`indigo-switch-label indigo-switch-label-right ${checked ? "indigo-switch-label-active" : ""}`}
          >
            {rightLabel}
          </span>
        )}
      </div>
    );
  }

  // Default switch without labels
  return (
    <div className={`indigo-switch ${variantClass} ${className}`}>
      <AntSwitch
        checked={checked}
        defaultChecked={defaultChecked}
        onChange={onChange}
        disabled={disabled}
        loading={loading}
        size={size}
        checkedChildren={checkedChildren}
        unCheckedChildren={unCheckedChildren}
        ref={ref}
        data-testid={dataTestId}
      />
    </div>
  );
}

// Create the forwardRef version with correct typings
export const Switch = forwardRef(InternalSwitch) as unknown as ((
  props: SwitchProps & {
    ref?: Ref<ComponentRef<typeof AntSwitch>>;
  },
) => ReactElement) & { displayName: string };

// Set displayName properly
Switch.displayName = "Switch";

export { switchVariants };
export default Switch;
